import { Box, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export interface UserResult {
  userId: string;
  name: string;
  email?: string;
  userType?: string;
  aliId?: string;
  artworkCount: number;
  profileImageUrl?: string;
  createdAt?: string;
}

interface UserResultCardProps {
  user: UserResult;
  onSelect: (user: UserResult) => void;
}

// Defense in depth — backend enforces this format, but we re-check before
// rendering attacker-controllable URLs.
const isSafeImageUrl = (url: string | undefined): url is string => {
  if (!url) return false;
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

export const UserResultCard = ({ user, onSelect }: UserResultCardProps) => {
  const theme = useTheme();
  const clickable = Boolean(user.aliId);
  const showImage = isSafeImageUrl(user.profileImageUrl);

  return (
    <Box
      data-testid={`user-result-${user.userId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        cursor: clickable ? 'pointer' : 'default',
        '&:hover': clickable ? { bgcolor: theme.palette.action.hover } : undefined,
      }}
      onClick={() => {
        if (clickable) onSelect(user);
      }}
    >
      <Box
        sx={{
          width: { xs: 40, sm: 48 },
          height: { xs: 40, sm: 48 },
          borderRadius: '50%',
          bgcolor: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
        }}
      >
        {showImage ? (
          <img
            src={user.profileImageUrl}
            alt={user.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Person sx={{ color: 'white', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}
        >
          {user.name}
        </Typography>
        {user.aliId && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            ALI ID: {user.aliId}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
        >
          {user.artworkCount} artworks
        </Typography>
      </Box>
    </Box>
  );
};
